import React, { useState, useEffect, useCallback } from 'react'
import { Button, CircularProgress } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CountryChartsMap from '../maps/ConwxPortfolio/CountryChartsMap'
import { transformPortfolioData } from '../utils/transformPortfolioData'
import Api from '../api'
import NoSite from '../components/NoSite'
import PortfolioInfoBox from '../common/dashboard/PortfolioInfoBox'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    // fill the area below the topbar, not the whole viewport: 100vh would
    // overflow it by the topbar's height and force a scrollbar
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default,
    // only scrolls once the window is too short even for a usable map, which
    // is the point at which hiding the content would be worse than scrolling
    overflow: 'auto',
  },
  container: {
    flex: 1,
    padding: '1rem 2rem 2rem',
    display: 'flex',
    flexDirection: 'column',
    // overflow: 'auto',
  },
  // the row above and the map below sit the same distance from the info boxes
  infoBoxGrid: {
    display: 'flex',
    marginBottom: '1rem',
  },
  latestUpdateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    alignSelf: 'flex-end',
    marginBottom: '1rem',
  },
  latestUpdate: {
    fontSize: 14,
    color: theme.palette.generalUi?.text || theme.palette.text.secondary,
  },
  infoBoxItem: {
    flex: '1 1 0',
    minWidth: 0,
    paddingLeft: '1rem',
    paddingRight: '1rem',
    '&:first-child': {
      paddingLeft: 0,
    },
    '&:last-child': {
      paddingRight: '1rem',
    },
  },
  /* flex:1 lets the map take exactly the space the rows above it leave over, so
     resizing anything above cannot push it off the page. It shrinks with the
     window down to minHeight, past which the page scrolls instead of squashing
     the map into something unusable. */
  mapWrapper: {
    flex: 1,
    minHeight: 320,
    '& > div': {
      height: '100%',
    },
  },
  centerItem: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
}))

const ConwxPortfolio = props => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadData = useCallback(async ({ force = false } = {}) => {
    setLoading(true)
    setError(null)
    try {
      const response = await Api.ConwxPortfolio.getConwxPortfolio({ force })
      // kW -> MW transform + reshape applied here
      setData(transformPortfolioData(response.data))
    } catch (err) {
      console.error('Failed to load ConWX Portfolio data:', err)
      setData(null)
      setError('The request failed. Use Refresh to try again.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const summary = data?.summary

  /* The whole page is one dataset, so until it arrives there is nothing to
     show: spin over everything. This covers the refresh too, where the data
     on screen is about to be replaced by the incoming one. */
  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.centerItem}>
          <CircularProgress />
        </div>
      </div>
    )
  }

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <div className={classes.latestUpdateRow}>
          {data?.latestUpdate && (
            <span className={classes.latestUpdate}>
              Latest update: {data.latestUpdate}
            </span>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={loading}
            onClick={() => loadData({ force: true })}
          >
            Refresh
          </Button>
        </div>
        {/* A failed request and an empty one are different things: say which. */}
        {error ? (
          <NoSite heading="Could not load the portfolio" body={error} />
        ) : !data ? (
          <NoSite
            heading="No portfolio data"
            body="The backend returned no data for the ConWx portfolio."
          />
        ) : null}
        {summary && (
          <div className={classes.infoBoxGrid}>
            <div className={classes.infoBoxItem}>
              <PortfolioInfoBox
                title="Number of Countries"
                content={summary.numberOfCountries}
              />
            </div>
            <div className={classes.infoBoxItem}>
              <PortfolioInfoBox
                title="Total Capacity (MW)"
                content={summary.totalCapacity}
              />
            </div>
            <div className={classes.infoBoxItem}>
              <PortfolioInfoBox
                title="Avg Monthly Production (MWh)"
                content={summary.avgMonthlyProduction}
              />
            </div>
            <div className={classes.infoBoxItem}>
              <PortfolioInfoBox
                title="Active Projects"
                content={summary.activeProjects}
              />
            </div>
          </div>
        )}
        {/* without countries the map can only wait forever, so keep it out */}
        {data?.countries && (
          <div className={classes.mapWrapper}>
            <CountryChartsMap countries={data.countries} />
          </div>
        )}
      </div>
    </div>
  )
}

export default ConwxPortfolio
